// Runtime status/reporting helpers for processed source entries.
// It updates datasets and logs critical compile/fetch diagnostics.
import type { BuildSourceEntry } from './types'
import { emitLentyLog } from '@lentystyle/guard/shared'

// Update element datasets for successful compile state.
export function markBuildSuccess(entry: BuildSourceEntry): void {
  // Check whether current source is an external stylesheet link.
  if (entry.source instanceof HTMLLinkElement) {
    entry.source.dataset.luisHref = entry.href
    entry.source.dataset.luisStatus = 'ok'
    // Return after link-source success updates.
    return
  }

  entry.source.dataset.luisSource = entry.rawSource
  entry.source.dataset.luisStatus = 'ok'
}

// Update element datasets and console output for compile/fetch errors.
export function markBuildError(entry: BuildSourceEntry, error: unknown, debugEnabled = false): void {
  const detail = extractErrorDetail(error)
  const lineSuffix = detail.line ? ` (line ${detail.line})` : ''
  // Emit one debug warning with source and line metadata when available.
  if (debugEnabled && detail.message) {
    emitLentyLog('RUN0023', buildDebugErrorParams(entry, detail))
  } else {
    // Skip debug warning emission when debug mode is disabled.
  }
  // Check whether current source is an external stylesheet link.
  if (entry.source instanceof HTMLLinkElement) {
    entry.source.dataset.luisHref = entry.href
    entry.source.dataset.luisStatus = 'error'
    emitLentyLog('RUN0024', { href: entry.href, lineSuffix }, { error })
    // Return after link-source error updates.
    return
  }

  entry.source.dataset.luisSource = entry.rawSource
  entry.source.dataset.luisStatus = 'error'
  emitLentyLog('RUN0025', { lineSuffix }, { error })
}

// Build human-readable source label used in runtime logs.
export function resolveSourceLabel(entry: BuildSourceEntry): string {
  // Check whether current source is an external stylesheet link.
  if (entry.kind === 'link') {
    // Return link href as the runtime source label.
    return entry.href
  } else {
    // Return inline source label for non-link entries.
    return 'inline data-luis style'
  }
}

// Build debug warning params with source and line metadata when available.
function buildDebugErrorParams(entry: BuildSourceEntry, detail: { message: string; line: number | null }): Record<string, string> {
  const sourceLabel = resolveSourceLabel(entry)
  const lineLabel = detail.line ? `:${detail.line}` : ''
  // Return normalized debug warning params for runtime error logs.
  return { sourceLabel, lineLabel, detailMessage: detail.message }
}

// Extract safe error detail from unknown runtime error payload.
function extractErrorDetail(error: unknown): { message: string; line: number | null } {
  const message = extractErrorMessage(error)
  // Return empty detail when error payload has no message text.
  if (!message) {
    // Return default detail for unsupported payload shapes.
    return { message: '', line: null }
  } else {
    // Continue line extraction from message text.
  }

  // Return normalized message detail with optional parsed line number.
  return { message, line: extractLineFromMessage(message) }
}

// Extract safe string message from unknown error payload.
function extractErrorMessage(error: unknown): string {
  // Use Error.message for Error objects.
  if (error instanceof Error) {
    // Return the native error message text.
    return error.message
  } else if (typeof error === 'string') {
    // Use string payload directly.
    return error
  } else {
    // Return empty string for unsupported payload shapes.
    return ''
  }
}

// Parse line number from common `.luis` parser/runtime message formats.
function extractLineFromMessage(message: string): number | null {
  // Regex for `line N` fragments. Example: `Invalid syntax near line 12`.
  const lineMatch = message.match(/\bline\s+(\d+)\b/i)
  // Return null when message contains no line number token.
  if (!lineMatch) {
    // Return null when no line token was parsed.
    return null
  } else {
    // Continue numeric line conversion.
  }

  const line = Number.parseInt(lineMatch[1], 10)
  // Return parsed line when it is valid and positive.
  if (Number.isFinite(line) && line > 0) {
    // Return the parsed positive line number.
    return line
  } else {
    // Return null when parsed line token is invalid.
    return null
  }
}
